import { FieldValidationResult, ValidationError } from './common-validation';
import {
  validateGaliCode,
  validateRequired,
  validateKatakana,
  getVisibleRepresentatives,
} from './company-shared-validation';
import { VALIDATION_MESSAGES, CONSTRUCTION_MESSAGES } from './validation-messages';

import type {
  ConstructionCompanyRegistrationForm,
  NoharaRepresentative,
} from '../../types/company.types';

// Individual field validations (reuse from customer)
export const validateGaliCustomerCode = (code: string): FieldValidationResult =>
  validateGaliCode(code, VALIDATION_MESSAGES.GALI_CODE_FORMAT);

export const validateOfficialName = (name: string): FieldValidationResult =>
  validateRequired(name, VALIDATION_MESSAGES.OFFICIAL_NAME_REQUIRED);

export const validateAbbreviatedName = (name: string): FieldValidationResult =>
  validateRequired(name, VALIDATION_MESSAGES.ABBREVIATED_NAME_REQUIRED);

export const validateCustomerNameKatakana = (name: string): FieldValidationResult =>
  validateKatakana(
    name,
    CONSTRUCTION_MESSAGES.COMPANY_NAME_KANA_REQUIRED,
    '取引先名カナは半角ｶﾅで入力ください。'
  );

// Representatives validation
// Validates only visible/editable representatives (canUpdate: true or undefined)
// Skips validation for records with canUpdate: false (they are hidden from UI)
export const validateRepresentatives = (
  representatives: NoharaRepresentative[]
): ValidationError[] => {
  const errors: ValidationError[] = [];

  // Filter to only count visible representatives for max count validation
  const visibleRepresentatives = getVisibleRepresentatives(representatives);

  if (visibleRepresentatives.length > 10) {
    errors.push({
      field: 'representatives',
      message: VALIDATION_MESSAGES.REPRESENTATIVES_MAX_COUNT,
    });
  }

  // Only validate visible/editable representatives
  // Skip validation for records with canUpdate: false (they are hidden from UI)
  for (const [index, rep] of representatives.entries()) {
    // Skip validation for records with canUpdate: false (they are hidden from UI)
    if (rep.canUpdate === false) {
      continue;
    }

    if (!rep.department?.trim()) {
      errors.push({
        field: `representatives.${index}.department`,
        message: VALIDATION_MESSAGES.REPRESENTATIVE_DEPARTMENT_REQUIRED,
      });
    }

    if (!rep.salesPerson?.id?.trim()) {
      errors.push({
        field: `representatives.${index}.salesPerson`,
        message: CONSTRUCTION_MESSAGES.REPRESENTATIVE_SALES_PERSON_REQUIRED,
      });
    }
  }

  return errors;
};

// Main form validation
export const validateConstructionRegistrationForm = (
  form: ConstructionCompanyRegistrationForm
): { isValid: boolean; errors: ValidationError[] } => {
  const errors: ValidationError[] = [];

  // Basic company info validation
  const galiResult = validateGaliCustomerCode(form.galiCustomerCode || '');
  if (!galiResult.isValid) {
    errors.push({ field: 'galiCustomerCode', message: galiResult.error });
  }

  const officialResult = validateOfficialName(form.officialName);
  if (!officialResult.isValid) {
    errors.push({ field: 'officialName', message: officialResult.error });
  }

  const abbreviatedResult = validateAbbreviatedName(form.abbreviatedName);
  if (!abbreviatedResult.isValid) {
    errors.push({ field: 'abbreviatedName', message: abbreviatedResult.error });
  }

  const katakanaResult = validateCustomerNameKatakana(form.nameKatakana);
  if (!katakanaResult.isValid) {
    errors.push({ field: 'nameKatakana', message: katakanaResult.error });
  }

  // Representatives validation
  const representativesErrors = validateRepresentatives(form.representatives);
  errors.push(...representativesErrors);

  return {
    isValid: errors.length === 0,
    errors,
  };
};
